import {memo, MouseEvent} from 'react';
import styled from 'styled-components';
import {MarkerContentContainer} from '@/map/components/Markers';
import {MAP_LAYER_Z_INDEX} from '@/map/constants';
import {Marker} from '@/map/modules';
import {interceptProps} from '@/styled-components';
import {SpotRoundMark, SpotRoundMarkProps} from './';
import {BigPointMarkerContent} from './BigSpotMarkerContent';
import {SpotName} from './SpotName';
import {Count} from './StyledComponents';

interface IProps extends naver.maps.MarkerOptions {
  id: string;
  name: string;
  active?: boolean;
  alwaysDisplayName?: boolean;
  count?: number;
  onClick?(e: MouseEvent<HTMLDivElement>): void;
}

export const SpotMarker = memo((props: IProps & SpotRoundMarkProps) => {
  const {
    id,
    name,
    color,
    icon,
    typeLabel,
    active,
    alwaysDisplayName,
    count,
    onClick,
    ...markerOptions
  } = props;

  if (!markerOptions.position) {
    return null;
  }

  return (
    <Marker clickable={true} draggable={false} {...markerOptions}>
      {active ? (
        <BigMarkerWrapper data-id={id} onClick={onClick}>
          <BigPointMarkerContent
            color={color}
            icon={icon}
            typeLabel={typeLabel}
            name={name}
            count={count ? count : 0}
          />
        </BigMarkerWrapper>
      ) : (
        <MarkerContentContainer>
          <SmallMarkerWrapper
            data-id={id}
            hasName={alwaysDisplayName}
            onClick={onClick}
          >
            <RoundShadow>
              <SpotRoundMark
                color={color}
                icon={icon}
                typeLabel={typeLabel}
                hasBorder
              />
              {!!count && <StyledCount>{count + 1}</StyledCount>}
            </RoundShadow>
            {alwaysDisplayName && <SpotName name={name} />}
          </SmallMarkerWrapper>
        </MarkerContentContainer>
      )}
    </Marker>
  );
});

const BigMarkerWrapper = styled.div`
  // 분석지역 센터 핀과 인덱스 동일하게 유지
  z-index: ${MAP_LAYER_Z_INDEX.LOCATION};
  position: relative;
`;

const SmallMarkerWrapper = styled('div').withConfig({
  shouldForwardProp: interceptProps(['hasName']),
})<{
  hasName?: boolean;
}>`
  display: flex;
  gap: 2px;
  flex-direction: column;
  align-items: center;
  position: relative;
  ${({hasName}) => hasName && `top: 20.2px;`}
`;

const RoundShadow = styled.div`
  filter: drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.2));
  border-radius: 100%;
  position: relative;
`;

const StyledCount = styled(Count)`
  right: -8px;
  top: -8px;
`;
